import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowLeft } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const transferred = searchParams.get("reason") === "transferred";
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => { if (user && user !== false) navigate(user.is_admin ? "/admin" : "/", { replace: true }); }, [user, navigate]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/request-code", { email: email.trim() });
      setStep("code");
      setCooldown(60);
      toast.success("Code sent — check your email");
    } catch (err) {
      toast.error(apiError(err, "Could not send code"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: email.trim(), code });
      setUser(data);
      navigate(data.is_admin ? "/admin" : "/", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Verification failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800 mb-5">Instructing.com</div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">PMP® Practice Exams</h1>
            <p className="mt-2 text-slate-500">12 full-length exams · Testing and Study modes</p>
          </div>

          {transferred && (
            <div data-testid="session-transferred-banner" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 leading-relaxed">
              You were signed out on this device because your account was signed in on another device
              (3-device limit). Sign in again to transfer access back to this device.
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_12px_rgba(0,0,0,0.05)] p-6 sm:p-8">
            {step === "email" ? (
              <form onSubmit={requestCode}>
                <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="email-input">
                  Email address
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    id="email-input"
                    data-testid="login-email-input"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                  />
                </div>
                <button
                  data-testid="request-code-btn"
                  type="submit"
                  disabled={busy}
                  className="mt-4 w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors"
                >
                  {busy ? "Sending…" : "Send sign-in code"}
                </button>
                <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                  Use the email tied to your PMP Master Program purchase. We&apos;ll send a 6-digit code —
                  you&apos;ll stay signed in on this device afterward.
                </p>
              </form>
            ) : (
              <form onSubmit={verify}>
                <button
                  type="button"
                  data-testid="back-to-email-btn"
                  onClick={() => { setStep("email"); setCode(""); }}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4 transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {email}
                </button>
                <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="otp-input">
                  Enter the 6-digit code
                </label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    id="otp-input"
                    data-testid="otp-code-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••••"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 font-mono text-lg tracking-[0.4em] text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                  />
                </div>
                <button
                  data-testid="verify-code-btn"
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="mt-4 w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors"
                >
                  {busy ? "Verifying…" : "Verify and sign in"}
                </button>
                <button
                  type="button"
                  data-testid="resend-code-btn"
                  disabled={cooldown > 0 || busy}
                  onClick={requestCode}
                  className="mt-3 w-full text-sm text-slate-500 hover:text-slate-700 disabled:text-slate-300 py-1 transition-colors"
                >
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                </button>
                <p className="mt-3 text-xs text-slate-400">Codes expire after 10 minutes.</p>
              </form>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            Access included with the PMP Master Program · 2-year license
          </p>
        </div>
      </div>
    </div>
  );
}
